/*
 * Hook de gestion du formulaire de création / édition d'une application.
 * Il centralise :
 * - le chargement des données
 * - l'initialisation du formulaire react-hook-form
 * - la synchronisation des données asynchrones en mode édition
 * - les actions de soumission
 */

import { skipToken } from '@reduxjs/toolkit/query'
import {
  useCreateApplicationMutation,
  useGetApplicationQuery,
  useUpdateApplicationMutation,
} from 'core/api/application'
import { toast } from 'core/components/common/Toast'
import {
  ERROR_DUPLICATE_NOM_APPLICATION,
  ERROR_DUPLICATE_NOM_TECHNIQUE,
} from 'core/constants/error'
import { ROUTE_PATHS } from 'core/constants/routePath'
import { UserContext } from 'core/contexts/UserContext'
import { ResponsablesMode } from 'core/interfaces/Application'
import { IIconeService, IService } from 'core/interfaces/Service'
import { getIconeInfo } from 'core/utils/service'
import { useGetAllServiceQuery } from 'lauria/api/service'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FieldErrors, Resolver, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router'
import { useParams } from 'react-router-dom'

export type ApplicationForm = {
  intitule: string
  nomTechnique: string
  description: string
  accroche: string
  idService: string
  tags: string
  responsablesMode?: ResponsablesMode
  responsablesUtilisateurIds: number[] | null
}

const DEFAULT_FORM_VALUES: ApplicationForm = {
  intitule: '',
  nomTechnique: '',
  description: '',
  accroche: '',
  idService: '',
  tags: '',
  responsablesMode: undefined,
  responsablesUtilisateurIds: null,
}

export const useEditApplication = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user: currentUser } = useContext(UserContext)
  const { idApplication } = useParams()

  const applicationId = idApplication ? parseInt(idApplication, 10) : undefined
  const creation = applicationId === undefined

  const [openIconSelector, setOpenIconSelector] = useState<boolean>(false)
  const [selectedService, setSelectedService] = useState<IService | null>(null)
  const [selectedTags, setSelectedTags] = useState<
    { id: number; label: string }[]
  >([])
  const [icone, setIcone] = useState<IIconeService | null>(null)
  const [selectedResponsables, setSelectedResponsables] = useState<
    { id: number; label: string }[]
  >([])

  const { data: services, isLoading: isLoadingServices } =
    useGetAllServiceQuery()

  const [createApplication, { isLoading: isLoadingCreateApplication }] =
    useCreateApplicationMutation()

  const [updateApplication, { isLoading: isLoadingUpdateApplication }] =
    useUpdateApplicationMutation()

  const {
    data: application,
    isLoading: isLoadingApp,
    isFetching,
  } = useGetApplicationQuery(applicationId ? { applicationId } : skipToken)

  const resolver: Resolver<ApplicationForm> = async (data) => {
    const errors: FieldErrors<ApplicationForm> = {}

    if (!data.intitule) {
      errors.intitule = { type: 'required', message: '' }
    }
    if (!data.description) {
      errors.description = { type: 'required', message: '' }
    }
    if (!data.accroche) {
      errors.accroche = { type: 'required', message: '' }
    }
    if (!data.nomTechnique) {
      errors.nomTechnique = { type: 'required', message: '' }
    }
    if (!data.idService) {
      errors.idService = { type: 'required', message: '' }
    }
    if (!data.tags) {
      errors.tags = { type: 'required', message: '' }
    }

    // Création seulement : responsablesMode obligatoire
    if (creation && !data.responsablesMode) {
      errors.responsablesMode = { type: 'required', message: '' }
    }

    // Dès qu'on choisit selected_users, la liste devient obligatoire
    if (
      data.responsablesMode === 'selected_users' &&
      (!data.responsablesUtilisateurIds ||
        data.responsablesUtilisateurIds.length === 0)
    ) {
      errors.responsablesUtilisateurIds = { type: 'required', message: '' }
    }

    return {
      values: Object.keys(errors).length > 0 ? {} : data,
      errors,
    }
  }

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid, dirtyFields, isSubmitted },
    setValue,
    getValues,
    reset,
    watch,
  } = useForm<ApplicationForm>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const responsablesMode = watch('responsablesMode')

  const state = useMemo(
    () => ({
      selectedService,
      setSelectedService,
      selectedTags,
      setSelectedTags,
      icone,
      setIcone,
      selectedResponsables,
      setSelectedResponsables,
    }),
    [selectedService, selectedTags, icone, selectedResponsables]
  )

  useEffect(() => {
    if (responsablesMode !== 'selected_users') {
      setSelectedResponsables([])
    }
  }, [responsablesMode])

  const shouldSkipNextTagsReset = useRef(false)

  useEffect(() => {
    if (!application) {
      return
    }

    const hydratedTags =
      application.tags?.map((tag) => ({
        id: tag.id,
        label: tag.nomTag,
      })) ?? []

    const hydratedResponsables =
      application.responsables
        ?.filter((responsable) => responsable.id !== currentUser?.idUtilisateur)
        .map((responsable) => ({
          id: responsable.id,
          label: `${responsable.prenom ?? ''} ${responsable.nom ?? ''}`.trim(),
        })) ?? []

    // Important :
    // react-hook-form ne met pas à jour les defaultValues automatiquement
    // quand les données arrivent après le premier render.
    reset({
      intitule: application.nomApplication ?? '',
      nomTechnique: application.nomTechnique ?? '',
      description: application.description ?? '',
      accroche: application.accroche ?? '',
      idService: application.service?.id?.toString() ?? '',
      tags: hydratedTags.length > 0 ? JSON.stringify(hydratedTags) : '',
      responsablesMode: application.responsablesMode ?? 'all_services_users',
      responsablesUtilisateurIds:
        hydratedResponsables.length > 0
          ? hydratedResponsables.map((responsable) => responsable.id)
          : null,
    })

    setSelectedService(application.service ?? null)

    // On ignore le prochain effet déclenché par le changement de tags
    shouldSkipNextTagsReset.current = true
    setSelectedTags(hydratedTags)

    setSelectedResponsables(hydratedResponsables)
    setIcone(application.icone ? getIconeInfo(application.icone) : null)
  }, [application, reset, currentUser?.idUtilisateur])

  useEffect(() => {
    setValue(
      'tags',
      selectedTags.length > 0 ? JSON.stringify(selectedTags) : '',
      { shouldValidate: isSubmitted }
    )
  }, [selectedTags, setValue, isSubmitted])

  useEffect(() => {
    setValue(
      'responsablesUtilisateurIds',
      selectedResponsables.length > 0
        ? selectedResponsables.map((responsable) => responsable.id)
        : null,
      { shouldValidate: isSubmitted }
    )
  }, [selectedResponsables, setValue, isSubmitted])

  useEffect(() => {
    if (shouldSkipNextTagsReset.current) {
      shouldSkipNextTagsReset.current = false
      return
    }

    setSelectedResponsables([])
  }, [selectedTags])

  const handleIntituleBlur = () => {
    if (application) {
      return
    }

    const intituleValue = getValues('intitule')
    if (!intituleValue) {
      return
    }

    if (!dirtyFields.accroche) {
      setValue(
        'accroche',
        t('applications.form.accrocheDefault', { intitule: intituleValue }),
        { shouldDirty: true, shouldValidate: true }
      )
    }

    if (!dirtyFields.nomTechnique) {
      const formattedValue = intituleValue
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')

      setValue('nomTechnique', formattedValue, {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }

  const handleSelectService = (service: IService) => {
    const nextService = selectedService?.id === service.id ? null : service

    setSelectedService(nextService)
    setValue('idService', nextService ? nextService.id.toString() : '', {
      shouldValidate: isSubmitted,
    })
  }

  const handleTagRemove = (id: number) => {
    setSelectedTags((prev) => prev.filter((tag) => tag.id !== id))
  }

  /**
   * Supprime un responsable de la liste des responsables sélectionnés
   */
  const handleResponsableRemove = (idResponsable: number) => {
    setSelectedResponsables((prev) =>
      prev.filter((responsable) => responsable.id !== idResponsable)
    )
  }

  const cancelAction = () => {
    if (creation) {
      navigate(generatePath(ROUTE_PATHS.DASHBOARD))
      return
    }

    navigate(
      generatePath(ROUTE_PATHS.PROMPTS, {
        idApplication: String(applicationId),
      })
    )
  }

  const catchErrors = (error: unknown) => {
    const typedError = error as {
      status?: number
      data?: { detail?: string }
    }

    if (typedError.status === 409) {
      if (typedError.data?.detail === ERROR_DUPLICATE_NOM_APPLICATION) {
        setError('intitule', {
          type: 'custom',
          message: t('applications.form.errors.duplicateIntitule'),
        })
      }

      if (typedError.data?.detail === ERROR_DUPLICATE_NOM_TECHNIQUE) {
        setError('nomTechnique', {
          type: 'custom',
          message: t('applications.form.errors.duplicateNomTechnique'),
        })
      }
    }
  }

  /**
   * Construit le payload des responsables en fonction du mode choisi
   */
  const buildResponsablesPayload = (
    data: ApplicationForm
  ): {
    responsablesMode?: ResponsablesMode
    responsablesUtilisateurIds?: number[]
  } => {
    if (creation) {
      if (data.responsablesMode === 'selected_users') {
        return {
          responsablesMode: data.responsablesMode,
          responsablesUtilisateurIds: data.responsablesUtilisateurIds ?? [],
        }
      }

      return data.responsablesMode
        ? { responsablesMode: data.responsablesMode }
        : {}
    }

    // En update, on n'envoie que si le champ a été modifié
    if (
      !data.responsablesMode ||
      (!dirtyFields.responsablesMode && !dirtyFields.responsablesUtilisateurIds)
    ) {
      return {}
    }

    if (data.responsablesMode === 'selected_users') {
      return {
        responsablesMode: data.responsablesMode,
        responsablesUtilisateurIds: data.responsablesUtilisateurIds ?? [],
      }
    }

    return {
      responsablesMode: data.responsablesMode,
    }
  }

  const onSubmit = handleSubmit((data) => {
    const responsablesPayload = buildResponsablesPayload(data)

    if (application) {
      updateApplication({
        idApplication: application.id,
        idService: parseInt(data.idService, 10),
        nomApplication: data.intitule,
        tags: selectedTags.map((tag) => tag.id),
        description: data.description,
        accroche: data.accroche,
        icone,
        ...responsablesPayload,
      })
        .unwrap()
        .then((updatedApplication) => {
          if (updatedApplication.id) {
            navigate(
              generatePath(ROUTE_PATHS.PROMPTS, {
                idApplication: updatedApplication.id,
              })
            )
            toast.success(t('applications.form.edit.success'))
          }
        })
        .catch(catchErrors)

      return
    }

    const idClient = currentUser?.idClient
    if (!idClient) {
      return
    }

    createApplication({
      idClient,
      idService: parseInt(data.idService, 10),
      nomApplication: data.intitule,
      nomTechnique: data.nomTechnique,
      tags: selectedTags.map((tag) => tag.id),
      description: data.description,
      accroche: data.accroche,
      icone,
      responsablesMode:
        responsablesPayload.responsablesMode as ResponsablesMode,
      responsablesUtilisateurIds:
        responsablesPayload.responsablesUtilisateurIds,
    })
      .unwrap()
      .then((createdApplication) => {
        if (createdApplication.id) {
          navigate(
            generatePath(ROUTE_PATHS.PROMPTS, {
              idApplication: createdApplication.id,
            })
          )
          toast.success(t('applications.form.success'))
        }
      })
      .catch(catchErrors)
  })

  const handleSelectIcon = (icon: IIconeService) => {
    setOpenIconSelector(false)
    setIcone(icon)
  }

  return {
    services,
    isLoading:
      isLoadingServices ||
      isLoadingApp ||
      isFetching ||
      isLoadingCreateApplication ||
      isLoadingUpdateApplication,
    state,
    handleSelectService,
    register,
    errors,
    handleSubmit,
    handleTagRemove,
    creation,
    isValid,
    cancelAction,
    onSubmit,
    handleIntituleBlur,
    openIconSelector,
    setOpenIconSelector,
    handleSelectIcon,
    handleResponsableRemove,
    responsablesMode,
  }
}
